import React, { useEffect, useMemo, useState } from "react";
import { TL } from "../i18n";
import client from "../lib/api";
import { Button } from "./ui";
import { toast } from "sonner";
import { num, money, holdingValue } from "../lib/holdingsMath";
import { HOLDINGS_CATEGORIES } from "../lib/holdingsTaxonomy";
import { TrendingUp, TrendingDown, Calculator, Target, Wallet, Save } from "lucide-react";

const TREND_MONTHS = [3, 6, 9, 12];
const monthsAgo = (m) => { const d = new Date(); d.setMonth(d.getMonth() - m); return d; };

// Category value folds in seeder roll-ups (gross value, appraised, principal, NFT).
function catTotals(rows) {
  const out = {};
  HOLDINGS_CATEGORIES.forEach((c) => (out[c] = { count: 0, total: 0 }));
  rows.forEach((r) => {
    const c = r.category;
    if (out[c]) { out[c].count += 1; out[c].total += holdingValue(r); }
  });
  return out;
}

export default function HoldingsSummary({ rows = [] }) {
  const totals = useMemo(() => catTotals(rows), [rows]);
  const grand = useMemo(() => HOLDINGS_CATEGORIES.reduce((a, c) => a + totals[c].total, 0), [totals]);

  const [snapshots, setSnapshots] = useState([]);
  const [computing, setComputing] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [fx, setFx] = useState("USD");
  const [targetVal, setTargetVal] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [savedTarget, setSavedTarget] = useState({ value: null, date: null });

  const loadSnaps = () => client.get("/holdings_snapshots")
    .then((r) => setSnapshots((r.data || []).map((x) => ({ id: x.id, ...(x.data || x) }))))
    .catch(() => {});

  useEffect(() => { loadSnaps(); }, []);
  useEffect(() => {
    client.get("/currencies").then((r) => setCurrencies((r.data || []).map((x) => ({ id: x.id, ...(x.data || x) })))).catch(() => {});
    client.get("/settings").then((r) => {
      const s = r.data || {};
      if (s.holdings_target_value != null) { setTargetVal(String(s.holdings_target_value)); setSavedTarget((p) => ({ ...p, value: s.holdings_target_value })); }
      if (s.holdings_target_date) { setTargetDate(s.holdings_target_date); setSavedTarget((p) => ({ ...p, date: s.holdings_target_date })); }
    }).catch(() => {});
  }, []);

  const compute = async () => {
    setComputing(true);
    try {
      const payload = { at: new Date().toISOString(), total: grand, categories: totals };
      await client.post("/holdings_snapshots", { data: payload });
      await loadSnaps();
      toast.success("Snapshot saved — trends will build up over time.");
    } catch (e) { toast.error("Could not save snapshot."); }
    setComputing(false);
  };

  // nearest snapshot at/older than the lookback point
  const trend = (cat, m) => {
    const cutoff = monthsAgo(m);
    const past = snapshots
      .filter((s) => s.at && new Date(s.at) <= cutoff && s.categories && s.categories[cat])
      .sort((a, b) => new Date(b.at) - new Date(a.at))[0];
    if (!past) return null;
    const before = num(past.categories[cat].total);
    const now = totals[cat].total;
    return { abs: now - before, pct: before ? ((now - before) / before) * 100 : null };
  };

  // ---- accumulated value with currency conversion ----
  const fxRate = fx === "USD" ? 1 : (num(currencies.find((c) => c.id === fx)?.rate) || 1);
  const fxLabel = fx === "USD" ? "USD" : (currencies.find((c) => c.id === fx)?.name || "");
  const converted = fxRate ? grand / fxRate : grand;

  const saveTarget = async () => {
    try {
      await client.put("/settings", { holdings_target_value: num(targetVal), holdings_target_date: targetDate || null });
      setSavedTarget({ value: num(targetVal), date: targetDate || null });
      toast.success("Target updated.");
    } catch (e) { toast.error("Could not save target."); }
  };

  const distance = savedTarget.value != null ? grand - savedTarget.value : null;
  const daysToTarget = savedTarget.date ? Math.round((new Date(savedTarget.date) - new Date()) / 86400000) : null;

  return (
    <div className="space-y-4" data-testid="holdings-summary">
      {/* ===== Top 3 boxes ===== */}
      <div className="grid gap-3 md:grid-cols-3">
        {/* Accumulated value + currency selector */}
        <div className="rounded-lg border border-black/10 bg-black/[0.02] p-4 dark:border-white/10 dark:bg-white/[0.03]" data-testid="hs-accumulated">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide opacity-60"><Wallet size={13} /> {TL("Accumulated value · all holdings")}</div>
            <select value={fx} onChange={(e) => setFx(e.target.value)} data-testid="hs-currency" className="rounded border border-black/15 bg-white/70 px-1 py-0.5 text-[11px] dark:border-white/15 dark:bg-black/30">
              <option value="USD">{TL("USD")}</option>
              {currencies.filter((c) => c.name).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="mt-1 font-head text-2xl tabular-nums" data-testid="hs-accumulated-value">{fxLabel} {converted.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
          {fx !== "USD" && <div className="text-[10px] opacity-55">Base {money(grand)} · rate {fxRate}</div>}
        </div>

        {/* Target (moving) */}
        <div className="rounded-lg border border-black/10 bg-black/[0.02] p-4 dark:border-white/10 dark:bg-white/[0.03]" data-testid="hs-target">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide opacity-60"><Target size={13} /> {TL("Target date value")}</div>
          <div className="mt-1.5 flex items-end gap-1.5">
            <label className="flex-1"><span className="text-[9px] uppercase opacity-55">{TL("Target")} ($)</span>
              <input type="number" value={targetVal} onChange={(e) => setTargetVal(e.target.value)} data-testid="hs-target-value" className="w-full rounded border border-black/15 bg-white/70 px-1.5 py-1 text-sm tabular-nums dark:border-white/15 dark:bg-black/30" /></label>
            <label className="flex-1"><span className="text-[9px] uppercase opacity-55">{TL("By date")}</span>
              <input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} data-testid="hs-target-date" className="w-full rounded border border-black/15 bg-white/70 px-1.5 py-1 text-sm dark:border-white/15 dark:bg-black/30" /></label>
            <Button variant="ghost" onClick={saveTarget} data-testid="hs-target-save" title={TL("Save target")}><Save size={15} /></Button>
          </div>
          {savedTarget.value != null && <div className="mt-1 text-[10px] opacity-60">Current target {money(savedTarget.value)}{savedTarget.date ? ` · by ${savedTarget.date}` : ""}</div>}
        </div>

        {/* Distance to target */}
        <div className="rounded-lg border border-black/10 bg-black/[0.02] p-4 dark:border-white/10 dark:bg-white/[0.03]" data-testid="hs-distance">
          <div className="text-[10px] uppercase tracking-wide opacity-60">{TL("Distance to target")}</div>
          {distance == null ? (
            <div className="mt-1 font-head text-2xl opacity-40">—</div>
          ) : (
            <>
              <div className={`mt-1 flex items-center gap-1.5 font-head text-2xl tabular-nums ${distance >= 0 ? "text-emerald-600" : "text-red-500"}`} data-testid="hs-distance-value">
                {distance >= 0 ? <TrendingUp size={20} /> : <TrendingDown size={20} />}
                {distance >= 0 ? "+" : "−"}{money(Math.abs(distance))}
              </div>
              <div className="text-[10px] opacity-60">{distance >= 0 ? "Ahead of target" : "Short of target"}{daysToTarget != null ? ` · ${daysToTarget >= 0 ? daysToTarget + " days left" : Math.abs(daysToTarget) + " days past"}` : ""}</div>
            </>
          )}
        </div>
      </div>

      {/* ===== 9 category boxes reading across ===== */}
      <div className="flex gap-3 overflow-x-auto pb-1" data-testid="hs-categories">
        {HOLDINGS_CATEGORIES.map((cat) => {
          const t = totals[cat];
          const imputed = t.count ? t.total / t.count : 0;
          return (
            <div key={cat} className="min-w-[196px] flex-shrink-0 rounded-lg border border-black/10 bg-white/40 p-3 dark:border-white/10 dark:bg-white/[0.03]" data-testid={`hs-cat-${cat}`}>
              <div className="truncate text-[11px] font-semibold" title={TL(cat)}>{TL(cat)}</div>
              <div className="mt-1.5 space-y-0.5 text-[11px]">
                <div className="flex justify-between"><span className="opacity-55">{TL("Records")}</span><span className="tabular-nums font-medium" data-testid={`hs-cat-count-${cat}`}>{t.count}</span></div>
                <div className="flex justify-between"><span className="opacity-55">{TL("Per record")}</span><span className="tabular-nums" data-testid={`hs-cat-imputed-${cat}`}>{money(imputed)}</span></div>
                <div className="flex justify-between border-t border-black/10 pt-0.5 dark:border-white/10"><span className="opacity-55">{TL("Total")}</span><span className="tabular-nums font-semibold" data-testid={`hs-cat-total-${cat}`}>{money(t.total)}</span></div>
              </div>
              <button onClick={compute} disabled={computing} data-testid={`hs-cat-compute-${cat}`} className="mt-2 flex w-full items-center justify-center gap-1 rounded border border-black/15 px-1.5 py-1 text-[10px] hover:bg-[var(--primary)] hover:text-white disabled:opacity-50 dark:border-white/15">
                <Calculator size={11} /> {computing ? TL("Saving…") : TL("Compute")}
              </button>
              <div className="mt-1.5 space-y-0.5 text-[10px]">
                {TREND_MONTHS.map((m) => {
                  const tr = trend(cat, m);
                  return (
                    <div key={m} className="flex justify-between" data-testid={`hs-cat-trend-${cat}-${m}`}>
                      <span className="opacity-55">{m} {TL("mo")}</span>
                      {tr == null
                        ? <span className="opacity-35">{TL("n/a")}</span>
                        : <span className={`tabular-nums ${tr.abs >= 0 ? "text-emerald-600" : "text-red-500"}`}>{tr.abs >= 0 ? "+" : "−"}{money(Math.abs(tr.abs))}{tr.pct != null ? ` (${tr.abs >= 0 ? "+" : ""}${tr.pct.toFixed(1)}%)` : ""}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
